import React, { Component } from "react";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomDatePicker from "components/DatePicker/CustomDatePicker.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";

import api from "api/index";

const styles = {
  formControl: { minWidth: "140px" },
  filterBar: { display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "16px", alignItems: "flex-end" }
};

const ACTION_TONES = {
  CREATE: "success",
  UPDATE: "info",
  DELETE: "danger",
  LOGIN: "muted",
  LOGOUT: "muted",
  APPROVE: "success",
  REJECT: "danger"
};

const ACTIONS = ["CREATE", "UPDATE", "DELETE", "LOGIN", "LOGOUT", "APPROVE", "REJECT"];
const ENTITIES = ["Student", "Staff", "Lecturer", "Payment", "Payroll", "Result", "Clearance", "Leave", "Asset", "Notice", "User"];

const columns = [
  {
    id: "timestamp",
    header: "Timestamp",
    accessorFn: l => (l.createdAt ? new Date(l.createdAt).toLocaleString() : "—")
  },
  {
    id: "user",
    header: "User",
    accessorFn: l => (l.user ? l.user.name || l.user.email || "System" : "System")
  },
  {
    id: "action",
    header: "Action",
    cell: info => {
      const l = info.row.original;
      const tone = ACTION_TONES[l.action] || "muted";
      return <Pill tone={tone}>{l.action || "—"}</Pill>;
    }
  },
  { id: "entity", header: "Entity", accessorFn: l => l.entity || "—" },
  { id: "entityId", header: "Entity ID", accessorFn: l => l.entityId || "—", meta: { variant: "mono" } },
  {
    id: "details",
    header: "Details",
    cell: info => {
      const l = info.row.original;
      const text = typeof l.details === "string" ? l.details : JSON.stringify(l.details || {});
      return (
        <span style={{ fontSize: "12px", color: "#555", maxWidth: "260px", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {text}
        </span>
      );
    }
  },
  { id: "ip", header: "IP", accessorFn: l => l.ipAddress || "—" }
];

class AuditLog extends Component {
  state = {
    logs: [],
    loading: false,
    total: 0,
    pagination: { pageIndex: 0, pageSize: 50 },
    actionFilter: "",
    entityFilter: "",
    dateFrom: "",
    dateTo: ""
  };

  componentDidMount() { this.fetchLogs(this.state.pagination); }

  fetchLogs = async (pagination = this.state.pagination) => {
    const { actionFilter, entityFilter, dateFrom, dateTo } = this.state;
    this.setState({ loading: true });
    try {
      const params = { page: pagination.pageIndex + 1, limit: pagination.pageSize };
      if (actionFilter) params.action = actionFilter;
      if (entityFilter) params.entity = entityFilter;
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      const res = await api.get("/audit-logs", { params });
      const d = res.data.data || res.data;
      const rows = Array.isArray(d) ? d : d.data || [];
      this.setState({ logs: rows, total: res.data.total || rows.length, pagination, loading: false });
    } catch (e) { this.setState({ loading: false }); }
  };

  handlePaginationChange = updater => {
    const next = typeof updater === "function" ? updater(this.state.pagination) : updater;
    this.fetchLogs(next);
  };

  render() {
    const { classes } = this.props;
    const { logs, loading, total, pagination, actionFilter, entityFilter, dateFrom, dateTo } = this.state;

    return (
      <GridContainer>
        <GridItem xs={12}>
          <div className={classes.filterBar}>
            <FormControl className={classes.formControl}>
              <InputLabel>Action</InputLabel>
              <Select value={actionFilter} onChange={e => this.setState({ actionFilter: e.target.value })}>
                <MenuItem value="">All Actions</MenuItem>
                {ACTIONS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl className={classes.formControl}>
              <InputLabel>Entity</InputLabel>
              <Select value={entityFilter} onChange={e => this.setState({ entityFilter: e.target.value })}>
                <MenuItem value="">All Entities</MenuItem>
                {ENTITIES.map(e => <MenuItem key={e} value={e}>{e}</MenuItem>)}
              </Select>
            </FormControl>
            <CustomDatePicker
              labelText="From"
              id="auditDateFrom"
              formControlProps={{ style: { minWidth: "140px" } }}
              inputProps={{ value: dateFrom, onChange: e => this.setState({ dateFrom: e.target.value }) }}
            />
            <CustomDatePicker
              labelText="To"
              id="auditDateTo"
              formControlProps={{ style: { minWidth: "140px" } }}
              inputProps={{ value: dateTo, onChange: e => this.setState({ dateTo: e.target.value }) }}
            />
            <Button size="sm" onClick={() => this.fetchLogs({ ...pagination, pageIndex: 0 })}
              style={{ background: "#1a237e", color: "#fff", marginBottom: "2px" }}>
              Apply
            </Button>
            <Button size="sm" simple onClick={() => this.setState({ actionFilter: "", entityFilter: "", dateFrom: "", dateTo: "" }, () => this.fetchLogs({ ...pagination, pageIndex: 0 }))}>
              Reset
            </Button>
          </div>

          <DataTable
            title="Audit Log"
            subtitle="All system mutations — read only"
            columns={columns}
            data={logs}
            isLoading={loading}
            emptyState="No audit log entries found."
            pagination={pagination}
            onPaginationChange={this.handlePaginationChange}
            totalCount={total}
          />
        </GridItem>
      </GridContainer>
    );
  }
}

AuditLog.propTypes = { classes: PropTypes.object.isRequired };
export default withStyles(styles)(AuditLog);
